<template>
    <v-container>
        <h2 class="mb-4">{{ mode === 'create' ? 'Create Sponsor' : mode === 'edit' ? 'Edit Sponsor' : 'View Sponsor' }}</h2>
        <div v-if="mode && !['edit', 'create'].includes(mode)" class="edit-button-wrapper">
            <v-btn class="mx-2" dark medium color="primary" :loading="navigating" :disabled="navigating || submitting || deletingImage" @click="onEdit">
                <v-icon dark>mdi-account-edit</v-icon>
            </v-btn>
        </div>
        <v-form :readonly="readOnly">
            <v-text-field
                v-model="sponsor.name"
                :rules="rules"
                label="Name"
                :clearable="clearable"
            />
            <v-text-field
                v-model.number="sponsor.priority"
                type="number"
                min="0"
                label="Priority"
                :clearable="clearable"
            />
            <v-text-field
                v-model="sponsor.website"
                label="Website"
                :clearable="clearable"
            />
            <v-file-input
                v-if="mode !== 'show'"
                v-model="newImage"
                label="Sponsor Logo"
                :rules="imageRules"
                filled
                show-size
                prepend-icon="mdi-camera"
                @change="createNewImagePreview"
            />
            <div v-if="previewImage && !previewImage.shouldDelete" class="image-input" :class="{'green-border': !previewImage.id}">
                <div v-if="mode === 'edit'" class="delete-btn">
                    <v-btn class="mx-2 white--text" small color="secondary" :loading="deletingImage" :disabled="deletingImage || submitting || navigating" @click="onImageDelete">
                        X
                    </v-btn>
                </div>
                <v-img v-if="previewImage.url" class="img-preview" :src="previewImage.url" contain height="180" />
            </div>
        </v-form>
        <div v-if="mode !== 'show'" class="submit-button-wrapper">
            <v-btn class="mx-2 white--text" :loading="submitting" :disabled="shouldDisableSubmit() || submitting || navigating || deletingImage" large color="primary" @click="onSubmit">
                Submit
            </v-btn>
        </div>
    </v-container>
</template>

<script>
    import sponsorService from '@/services/sponsor-service'

    export default {
        name: 'SponsorForm',
        data: () => {
            return {
                mode: null,
                readOnly: false,
                clearable: true,
                apiUrl: process.env.VUE_APP_API_HOST_DOMAIN,
                sponsor: {
                    name: '',
                    priority: null,
                    website: ''
                },
                sponsorId: null,
                rules: [
                    v => !!v || 'Name is required'
                ],
                newImage: null,
                previewImage: null,
                imageRules: [
                    value => !value || value.size < 2000000 || 'Image size should be less than 2 MB!'
                ],
                submitting: false,
                navigating: false,
                deletingImage: false
            }
        },
        created() {
            this.mode = this.$route.params.mode
            if (this.mode === 'create') {
                return
            } else if (this.mode === 'show') {
                this.readOnly = true
                this.clearable = false
            }
            this.sponsorId = this.$route.params.sponsorId
            if (this.sponsorId) {
                sponsorService.getSponsorById(this.sponsorId).then(resp => {
                    this.sponsor = {
                        ...resp.data.sponsor,
                        name: resp.data.sponsor.title
                    }
                    if (this.sponsor.imageName) {
                        this.previewImage = {
                            id: this.sponsor.id,
                            url: process.env.VUE_APP_GOOGLE_BUCKET_URL + this.sponsor.imageName,
                            imageName: this.sponsor.imageName
                        }
                    }
                })
            }
        },
        methods: {
            shouldDisableSubmit() {
                return !this.sponsor.name
            },
            async onEdit() {
                this.navigating = true
                try {
                    await this.$router.push(`/sponsors/${this.sponsorId}/edit`)
                } finally {
                    this.navigating = false
                }
            },
            async createSponsor() {
                const formData = new FormData()
                formData.append('title', this.sponsor.name)
                if (this.sponsor.website) {
                    formData.append('website', this.sponsor.website)
                }
                if (this.sponsor.priority !== null && this.sponsor.priority !== undefined && this.sponsor.priority !== '') {
                    formData.append('priority', this.sponsor.priority)
                }
                if (this.hasSelectedImage(this.newImage)) {
                    formData.append('file', this.newImage)
                }
                const response = await sponsorService.createSponsor(formData)
                if (response.data.id) {
                    this.sponsorId = response.data.id
                }
                this.$router.push(`/sponsors`)
            },
            async editSponsor() {
                const formData = new FormData()
                formData.append('title', this.sponsor.name)
                if (this.sponsor.website) {
                    formData.append('website', this.sponsor.website)
                }
                if (this.sponsor.priority !== null && this.sponsor.priority !== undefined && this.sponsor.priority !== '') {
                    formData.append('priority', this.sponsor.priority)
                }
                if (this.hasSelectedImage(this.newImage)) {
                    formData.append('file', this.newImage)
                }
                if (this.previewImage && this.previewImage.id && this.previewImage.shouldDelete) {
                    formData.append('removeImage', 'true')
                }
                await sponsorService.editSponsorById(this.sponsorId, formData)
                this.$router.push(`/sponsors`)
            },
            async onSubmit() {
                this.submitting = true
                try {
                    if (this.mode === 'edit') {
                        await this.editSponsor()
                    } else if (this.mode === 'create') {
                        await this.createSponsor()
                    }
                    this.deleteSponsorImage()
                } finally {
                    this.submitting = false
                }
            },
            deleteSponsorImage() {
                if (this.previewImage && this.previewImage.id && this.previewImage.shouldDelete) {
                    this.previewImage = null
                }
            },
            createNewImagePreview() {
                if (!this.hasSelectedImage(this.newImage)) {
                    this.previewImage = null
                    return
                }
                this.previewImage = this.newImage
                this.previewImage.url = URL.createObjectURL(this.newImage)
            },
            onImageDelete() {
                this.deletingImage = true
                if (this.previewImage && this.previewImage.id) {
                    this.previewImage.shouldDelete = true
                    this.previewImage.url = null
                    this.previewImage = { ...this.previewImage }
                } else {
                    this.newImage = null
                    this.previewImage = null
                }
                this.deletingImage = false
            },
            hasSelectedImage(image) {
                return image && typeof image === 'object' && image.size !== undefined && image.name
            }
        }
    }
</script>

<style scoped>
.edit-button-wrapper {
    width: 100%;
    display: flex;
    justify-content: flex-end;
}

.submit-button-wrapper {
    width: 100%;
    display: flex;
    justify-content: center;
    margin-top: 2vh;
}

.image-input {
    border: 2px dashed #ccc;
    padding: 20px;
    border-radius: 4px;
    margin-top: 20px;
    position: relative;
}

.image-input.green-border {
    border-color: #4caf50;
}

.delete-btn {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 10;
}

.img-preview {
    width: 100%;
    max-width: 100%;
    height: 180px;
    margin: 0 auto 10px;
}
</style>


